// @ts-nocheck - Temporary fix for provider service types
import { backendProviderService } from './backendProviderService';
import type { ServiceProvider, User } from '@/types';

class ProviderService {
  // Get provider profile from user data - now uses real backend API
  async getProviderProfile(user: User): Promise<ServiceProvider | null> {
    try {
      // First try to get existing provider profile
      const providers = await backendProviderService.getProviders({ 
        userId: user.id 
      });
      
      if (providers.length > 0) {
        return providers[0];
      }

      // If no provider profile exists and user is a service provider, create one
      if (user.role === 'provider') {
        const providerData = {
          userId: user.id,
          businessName: user.businessName || `${user.firstName} ${user.lastName}`,
          city: 'Dublin', // Default - should be from user profile
          county: 'Dublin', // Default - should be from user profile
          services: [], // Should be from user preferences
          description: `Professional service provider - ${user.firstName} ${user.lastName}`
        };

        await backendProviderService.createProvider(providerData);
        
        // Fetch the newly created provider
        const newProviders = await backendProviderService.getProviders({ 
          userId: user.id 
        });
        
        return newProviders.length > 0 ? newProviders[0] : null;
      }

      return null;
    } catch (error) {
      console.error('Error getting provider profile:', error);
      return null;
    }
  }

  // Check if provider is a company
  isCompanyProvider(user: User): boolean {
    const providerType = typeof user.providerType === 'string' ? user.providerType.toLowerCase() : '';
    return providerType === 'company' || user.role === 'company';
  }

  // Get features available to provider type
  getAvailableFeatures(user: User): {
    teamManagement: boolean;
    advancedAnalytics: boolean;
    bulkOperations: boolean;
    multiLocation: boolean;
    customBranding: boolean;
    apiAccess: boolean;
  } {
    const isCompany = this.isCompanyProvider(user);
    const isPremium = user.isPremium || false;
    const isEnterprise = user.subscriptionTier === 'ENTERPRISE';

    return {
      teamManagement: isCompany, // Only companies can manage teams
      advancedAnalytics: isPremium, // Premium feature
      bulkOperations: isCompany && isPremium, // Company + Premium
      multiLocation: isCompany, // Companies can have multiple locations
      customBranding: isEnterprise, // Enterprise feature
      apiAccess: isEnterprise // Enterprise feature
    };
  }

  // Get dashboard configuration based on provider type
  getDashboardConfig(user: User): {
    quickActions: Array<{
      id: string;
      title: string;
      description: string;
      icon: string;
      href: string;
      available: boolean;
      premium?: boolean;
    }>;
    widgets: Array<{
      id: string;
      title: string;
      available: boolean;
      premium?: boolean;
    }>;
  } {
    const isCompany = this.isCompanyProvider(user);
    const isPremium = user.isPremium || false;
    const features = this.getAvailableFeatures(user);

    const quickActions = [
      {
        id: 'jobs',
        title: 'Available Jobs',
        description: isPremium 
          ? 'Premium access to all customer job postings' 
          : 'Upgrade to access customer job postings',
        icon: 'Briefcase',
        href: '/jobs',
        available: true,
        premium: !isPremium
      },
      {
        id: 'calendar',
        title: 'Calendar & Bookings',
        description: 'Manage your schedule and availability',
        icon: 'Calendar',
        href: '/calendar',
        available: true
      },
      {
        id: 'analytics',
        title: 'Analytics & Customers',
        description: 'Track performance and manage relationships',
        icon: 'BarChart3',
        href: '/analytics',
        available: true
      },
      {
        id: 'team',
        title: 'Team Management',
        description: isCompany 
          ? 'Manage your team members and assignments'
          : 'Available for company accounts only',
        icon: 'Users',
        href: '/team',
        available: features.teamManagement
      },
      {
        id: 'invoices',
        title: 'Invoices',
        description: 'Manage billing and track payments',
        icon: 'FileText',
        href: '/invoices',
        available: true
      },
      {
        id: 'quote-templates',
        title: 'Quote Templates',
        description: 'Create and manage reusable quote templates',
        icon: 'FileText',
        href: '/quote-templates',
        available: true
      }
    ];

    const widgets = [
      {
        id: 'revenue',
        title: 'Revenue Overview',
        available: true
      },
      {
        id: 'bookings',
        title: 'Recent Bookings',
        available: true
      },
      {
        id: 'team-performance',
        title: 'Team Performance',
        available: features.teamManagement
      },
      {
        id: 'customer-insights',
        title: 'Customer Insights',
        available: features.advancedAnalytics,
        premium: true
      }
    ];

    return { quickActions, widgets };
  }

  // Get team management capabilities
  getTeamCapabilities(user: User): {
    maxTeamMembers: number;
    canAssignJobs: boolean;
    canTrackPerformance: boolean;
    canManageSchedules: boolean;
    canSetPermissions: boolean;
  } {
    const isCompany = this.isCompanyProvider(user);
    const isPremium = user.isPremium || false;
    const isEnterprise = user.subscriptionTier === 'ENTERPRISE';

    if (!isCompany) {
      return {
        maxTeamMembers: 0,
        canAssignJobs: false,
        canTrackPerformance: false,
        canManageSchedules: false,
        canSetPermissions: false
      };
    }

    return {
      maxTeamMembers: isEnterprise ? 50 : isPremium ? 15 : 5,
      canAssignJobs: true,
      canTrackPerformance: isPremium,
      canManageSchedules: true,
      canSetPermissions: isPremium
    };
  }
}

export const providerService = new ProviderService();